/**
 * Parent-side dispatcher.
 *
 * Expands an event via the registry, executes parent-kind commands locally,
 * and posts iframe-kind commands to the widget iframe via postMessage.
 *
 * Security:
 * - postMessage targetOrigin is always derived from iframe.src (never '*').
 * - Unknown events throw (caller surfaces the error).
 * - Invoke only after iframe handshake — caller responsible (mirrors how
 *   iframe→parent scrollTo works without queueing).
 */
import type { Command, CommandMessage, EventParams, WidgetInstance } from './types';
import { COMMAND_MESSAGE_TYPE } from './config';
import { expandEvent } from './event-registry';

export const dispatchEvent = (
  name: string,
  params: EventParams,
  widgets: Map<string, WidgetInstance>
): void => {
  const commands = expandEvent(name, params);
  widgets.forEach((widget) => {
    commands.forEach((cmd) => executeCommand(cmd, widget));
  });
};

const executeCommand = (cmd: Command, widget: WidgetInstance): void => {
  if (cmd.kind === 'parent') {
    executeParentCommand(cmd, widget);
    return;
  }
  postIframeCommand(cmd, widget);
};

const executeParentCommand = (
  cmd: Extract<Command, { kind: 'parent' }>,
  widget: WidgetInstance
): void => {
  if (cmd.action === 'widget.scrollIntoView') {
    widget.iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const postIframeCommand = (
  cmd: Extract<Command, { kind: 'iframe' }>,
  widget: WidgetInstance
): void => {
  const targetOrigin = new URL(widget.iframe.src).origin;
  const message: CommandMessage = {
    type: COMMAND_MESSAGE_TYPE,
    widgetId: widget.widgetId,
    action: cmd.action,
    payload: cmd.payload,
  };
  widget.iframe.contentWindow?.postMessage(message, targetOrigin);
};
